import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTier } from "../utils/tierContext";
import { useUser } from "../utils/userContext";
import { isOwner } from "../utils/access";
import styles from "./SettingsLayout.module.css";

interface SettingsNavItem {
  key: string;
  label: string;
  to: string;
  feature?: string;
  featureLevel?: string;
  ownerOnly?: boolean;
  minTier?: string;
}

interface SettingsGroup {
  kind: "group";
  key: string;
  label: string;
  items: SettingsNavItem[];
}

interface SettingsLink {
  kind: "link";
  key: string;
  label: string;
  to: string;
  feature?: string;
  ownerOnly?: boolean;
}

type SettingsEntry = SettingsGroup | SettingsLink;

const settingsEntries: SettingsEntry[] = [
  {
    kind: "group",
    key: "organization",
    label: "Organization",
    items: [
      { key: "company", label: "Company Info", to: "/settings/company" },
      { key: "users", label: "Manage Users", to: "/settings/users" },
    ],
  },
  {
    kind: "group",
    key: "customization",
    label: "Customization",
    items: [
      { key: "jobtypes", label: "Job Types", to: "/settings/jobtypes", feature: "custom_job_types" },
      { key: "expensetypes", label: "Expense Types", to: "/settings/expensetypes", feature: "custom_expense_categories" },
      { key: "billingroles", label: "Billing Roles", to: "/settings/billingroles", feature: "billing_controls", featureLevel: "advanced" },
      { key: "visibility", label: "Visibility", to: "/settings/visibility", minTier: "core" },
      { key: "features", label: "Features", to: "/settings/features" },
    ],
  },
  {
    kind: "link",
    key: "billing",
    label: "Subscription",
    to: "/settings/billing",
    ownerOnly: true,
  },
  {
    kind: "link",
    key: "audit",
    label: "Audit History",
    to: "/settings/audit",
    feature: "audit_history",
  },
];

const SettingsLayout: React.FC = () => {
  const { tier, hasFeature, getFeatureLevel } = useTier();

  const TIER_RANK: Record<string, number> = { free_crew: 0, core: 1, pro: 2, enterprise: 3 };

  const meetsTier = (minTier?: string) => {
    if (!minTier) return true;
    return (TIER_RANK[tier] ?? 0) >= (TIER_RANK[minTier] ?? 0);
  };
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();

  const userIsOwner = isOwner(user?.roleId ?? 0);

  const LEVEL_ORDER: Record<string, number> = { standard: 1, advanced: 2 };

  const meetsFeatureLevel = (item: { feature?: string; featureLevel?: string }) => {
    if (!item.feature) return true;
    if (!hasFeature(item.feature)) return false;
    if (!item.featureLevel) return true;
    const current = getFeatureLevel(item.feature);
    return (LEVEL_ORDER[current ?? ""] ?? 0) >= (LEVEL_ORDER[item.featureLevel] ?? 0);
  };

  // Filter out entries/items gated by features, role, or tier
  const visibleEntries = settingsEntries
    .map((entry) => {
      if (entry.kind === "link") {
        if (entry.ownerOnly && !userIsOwner) return null;
        return meetsFeatureLevel(entry) ? entry : null;
      }
      const items = entry.items.filter(
        (item) =>
          (!item.ownerOnly || userIsOwner) &&
          meetsTier(item.minTier) &&
          meetsFeatureLevel(item)
      );
      return items.length > 0 ? { ...entry, items } : null;
    })
    .filter(Boolean) as SettingsEntry[];

  // Find which group contains the active page
  const activeGroupKey = visibleEntries.find(
    (e) => e.kind === "group" && e.items.some((item) => location.pathname === item.to)
  )?.key;

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (activeGroupKey) {
      initial.add(activeGroupKey);
    } else {
      const firstGroup = visibleEntries.find((e) => e.kind === "group");
      if (firstGroup) initial.add(firstGroup.key);
    }
    return initial;
  });

  // Keep active group expanded when navigating
  React.useEffect(() => {
    if (activeGroupKey && !expandedGroups.has(activeGroupKey)) {
      setExpandedGroups((prev) => new Set(prev).add(activeGroupKey));
    }
  }, [activeGroupKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  return (
    <div className="main-articles company-articles">
      <div className={styles["settings-container"]}>
        <div className={styles["settings-mobile-nav"]}>
          <label className={styles["settings-mobile-label"]} htmlFor="settingsMobileNav">
            Settings area
          </label>
          <select
            id="settingsMobileNav"
            className={styles["settings-mobile-select"]}
            value={visibleEntries.some((entry) =>
              entry.kind === "link"
                ? location.pathname === entry.to
                : entry.items.some((item) => location.pathname === item.to)
            ) ? location.pathname : ""}
            onChange={(event) => {
              if (event.target.value) navigate(event.target.value);
            }}
          >
            {visibleEntries.map((entry) => {
              if (entry.kind === "link") {
                return (
                  <option key={entry.key} value={entry.to}>
                    {entry.label}
                  </option>
                );
              }

              return (
                <optgroup key={entry.key} label={entry.label}>
                  {entry.items.map((item) => (
                    <option key={item.key} value={item.to}>
                      {item.label}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
        <nav className={styles["settings-sidebar"]}>
          <div className={styles["settings-nav"]}>
            {visibleEntries.map((entry) => {
              if (entry.kind === "link") {
                return (
                  <div key={entry.key} className={styles["settings-group"]}>
                    <NavLink
                      to={entry.to}
                      end
                      className={({ isActive }) =>
                        `${styles["settings-direct-link"]} ${isActive ? styles["settings-direct-link-active"] : ""}`
                      }
                    >
                      {entry.label}
                    </NavLink>
                  </div>
                );
              }

              const isExpanded = expandedGroups.has(entry.key);
              return (
                <div key={entry.key} className={styles["settings-group"]}>
                  <button
                    type="button"
                    className={styles["settings-group-header"]}
                    onClick={() => toggleGroup(entry.key)}
                    aria-expanded={isExpanded}
                  >
                    <span>{entry.label}</span>
                    <svg
                      className={`${styles["settings-group-chevron"]} ${isExpanded ? styles["settings-group-chevron-open"] : ""}`}
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <ul className={styles["settings-group-items"]}>
                      {entry.items.map((item) => (
                        <li key={item.key}>
                          <NavLink
                            to={item.to}
                            end
                            className={({ isActive }) =>
                              `${styles["settings-nav-item"]} ${isActive ? styles["settings-nav-item-active"] : ""}`
                            }
                          >
                            {item.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className={styles["settings-content"]}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SettingsLayout;
